import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { compact, flatten, sortBy } from "lodash"
import type {
    AccessData,
    AccountImage,
    Customer,
    InventoryItem,
    LoyaltyProgramData,
    Setting,
    UserAccount,
} from "./types"
import type { StateObject } from "../redux/types"
import type { DataState } from "./reducer"
import { getCurrentAgenda, getCurrentPath, getCurrentPathName } from "../router/selectors"
import { getLocationsAuthData } from "../auth/selectors"
import { MainMenuAgendaEnum } from "../router/types"
import type { TerminalType } from "../../graphql/types"
import { AuthLocation } from "../../graphql/types"
import { MultiSelectItem } from "../uiKit/MultiSelect/MultiSelect"
import { LocationAuthData } from "../auth/types"

export const getDataStateObject = (state: StateObject): DataState => state.data

export const getSettings = (state: StateObject): Setting[] => getDataStateObject(state).settings

export const getCustomers = (state: StateObject): Customer[] => getDataStateObject(state).customers

export const getInvetoryItems = (state: StateObject): InventoryItem[] => getDataStateObject(state).inventoryItems

export const getAccountImages = (state: StateObject): AccountImage[] => getDataStateObject(state).accountImages
// NOTE For now, we support only one Loyalty Program. More will be in future

export const getLoyaltyProgramFormData = (state: StateObject): Maybe<LoyaltyProgramData> => {
    const data = getDataStateObject(state).loyaltyPrograms

    if (data && data.length > 0) {
        return data[0]
    }

    if (data && data.length === 0) {
        return undefined
    }

    return null
}

export const getInventoryItemById = (state: StateObject, inventoryItemId: Maybe<string>): Maybe<InventoryItem> =>
    getInvetoryItems(state).find(i => i.id === inventoryItemId)

export const getDataForTerminalsMultiselect = (
    state: StateObject,
    terminalTypes?: TerminalType[]
): MultiSelectItem[] => {
    const locations = getLocationsAuthData(state)
    return transformMultiPickerData(locations, terminalTypes)
}

export const transformMultiPickerData = (
    locations: LocationAuthData[] | AuthLocation[],
    terminalTypes?: TerminalType[]
): MultiSelectItem[] =>
    flatten(
        compact(
            locations.map(l => {
                const terminals = terminalTypes ? l.terminals.filter(t => terminalTypes.includes(t.type)) : l.terminals
                if (terminals.length === 0) {
                    return null
                }
                return {
                    id: l.id,
                    label: l.name,
                    labelOnly: true,
                    children: terminals.map(t => ({
                        id: t.id,
                        label: t.name,
                    })),
                }
            })
        )
    )

// User Account

export const getUserAccounts = (state: StateObject): UserAccount[] => getDataStateObject(state).userAccounts
// User Account

export const getUserAccountsSortedByUserName = (state: StateObject): UserAccount[] =>
    sortBy(getUserAccounts(state), "firstName")

export const getUserAccountById = (state: StateObject, userAccountId: Maybe<string>): Maybe<UserAccount> =>
    getUserAccounts(state).find(u => u.id === userAccountId)

// Access and roles

export const getAccesses = (state: StateObject): AccessData[] => getDataStateObject(state).accesses

export const getAdminAccessData = (state: StateObject): Maybe<AccessData> =>
    getAccesses(state).find(a => a.name.toLowerCase().includes("admin"))

export const isItemsAndCategoriesAgendaOpened = (state: StateObject) => getCurrentPathName(state) === "/app/menu"

export const isInventoryAgendaOpened = (state: StateObject) => getCurrentPathName(state) === "/app/inventory"

export const isLocationsAndTerminalsAgendaOpened = (state: StateObject) =>
    getCurrentPathName(state) === "/app/locations"

export const isAssignmentsAgendaOpened = (state: StateObject) => getCurrentPathName(state) === "/app/assignments"

export const isPricingsAgendaOpened = (state: StateObject) => getCurrentAgenda(state) === MainMenuAgendaEnum.Pricing

export const isPromotionsAgendaOpened = (state: StateObject) =>
    getCurrentAgenda(state) === MainMenuAgendaEnum.Promotions

export const isEmployeesAgendaOpened = (state: StateObject) => getCurrentPath(state).asPath === "/employees/table"

export const isSummaryAgendaOpened = (state: StateObject) => getCurrentPathName(state) === "/app/reports/summary"

export const isExtensiveReportsAgendaOpened = (state: StateObject) =>
    getCurrentPathName(state) === "/app/reports/extensiveReports"

export const isSchematizedAgendaOpened = (state: StateObject) => getCurrentPathName(state) === "/app/salesInput"

export const isReceiptSettingsAgendaOpened = (state: StateObject) =>
    getCurrentPathName(state) === "/app/receiptSettings"

export const isPaymentSettingsAgendaOpened = (state: StateObject) =>
    getCurrentPathName(state) === "/app/paymentSettings"

export const isSpecialPricingReportAgendaOpened = (state: StateObject) =>
    getCurrentAgenda(state) === MainMenuAgendaEnum.SpecialPricingReport

export const isVoidsReportAgendaOpened = (state: StateObject) =>
    getCurrentAgenda(state) === MainMenuAgendaEnum.VoidsReport

export const isLoyaltyProgramAgendaOpened = (state: StateObject) => {
    return getCurrentPath(state).asPath === "/loyalty-program"
}

export const areSettingsDataLoaded = (state: StateObject): boolean => {
    return getSettings(state).length > 0
}
